import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextArea, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import type {
  CompanyDocument,
  Question,
  QuestionCategory,
  QuestionType,
} from '../lib/types.ts';

type DraftTrigger = {
  trigger_value: string;
  document_id: string;
  adopt_only: boolean;
};

type QuestionDraft = {
  type: QuestionType;
  category_id: string;
  label: string;
  help_text: string;
  required: boolean;
  requires_upload: boolean;
  upload_label: string;
  options: string[];
  parent_id: string | null;
  condition_value: string;
  adoptable_doc_id: string | null;
  adoption_triggers: DraftTrigger[];
  no_followup_ids: string[];
  // Per-option follow-ups for multiselect / multiselect_uploads parents.
  // Map from option string → list of child question ids that should appear
  // (and be required) when that option is ticked.
  option_followup_ids: Record<string, string[]>;
};

function emptyDraft(defaultCategoryId: string): QuestionDraft {
  return {
    type: 'yesno',
    category_id: defaultCategoryId,
    label: '',
    help_text: '',
    required: true,
    requires_upload: false,
    upload_label: '',
    options: [],
    parent_id: null,
    condition_value: '',
    adoptable_doc_id: null,
    adoption_triggers: [],
    no_followup_ids: [],
    option_followup_ids: {},
  };
}

export function AdminQuestionBuilder() {
  const qc = useQueryClient();
  const questionsQuery = useQuery({
    queryKey: ['questions'],
    queryFn: () => adminApi.get<{ questions: Question[] }>('/api/questions'),
  });
  const documentsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: () => adminApi.get<{ documents: CompanyDocument[] }>('/api/documents'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['question-categories'],
    queryFn: () =>
      adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories'),
  });

  const [editing, setEditing] = useState<Question | null>(null);
  const [creatingInCategory, setCreatingInCategory] = useState<string | null>(null);
  const [optimisticOrder, setOptimisticOrder] = useState<string[] | null>(null);

  const questions = questionsQuery.data?.questions ?? [];
  const documents = documentsQuery.data?.documents ?? [];
  const categories = categoriesQuery.data?.categories ?? [];

  const ordered = useMemo(() => {
    if (!optimisticOrder) return questions;
    const map = new Map(questions.map((q) => [q.id, q]));
    return optimisticOrder.map((id) => map.get(id)!).filter(Boolean);
  }, [questions, optimisticOrder]);

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.post('/api/questions/reorder', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questions'] });
      setOptimisticOrder(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/questions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['questions'] }),
  });

  function moveWithinCategory(categoryId: string, id: string, dir: -1 | 1) {
    const inCategory = ordered.filter((q) => q.category_id === categoryId).map((q) => q.id);
    const idx = inCategory.indexOf(id);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= inCategory.length) return;
    const swapped = [...inCategory];
    [swapped[idx], swapped[next]] = [swapped[next]!, swapped[idx]!];
    // Splice back into the full ordering so display_order changes only within the category.
    const fullIds = ordered.map((q) => q.id);
    let j = 0;
    const merged = fullIds.map((qid) => {
      const q = questions.find((x) => x.id === qid)!;
      if (q.category_id === categoryId) return swapped[j++]!;
      return qid;
    });
    setOptimisticOrder(merged);
    reorderMutation.mutate(merged);
  }

  if (questionsQuery.isLoading || documentsQuery.isLoading || categoriesQuery.isLoading) {
    return <CenteredSpinner />;
  }

  if (categories.length === 0) {
    return (
      <EmptyState
        title="No categories yet"
        description="Create at least one category under Settings before adding questions."
      />
    );
  }

  const grouped = new Map<string, Question[]>();
  for (const cat of categories) grouped.set(cat.id, []);
  for (const q of ordered) {
    const list = grouped.get(q.category_id);
    if (list) list.push(q);
  }

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Question builder</h1>
          <p className="mt-1 text-sm text-slate-500">
            {questions.length} question{questions.length === 1 ? '' : 's'} across {categories.length}{' '}
            categor{categories.length === 1 ? 'y' : 'ies'}
          </p>
        </div>
      </div>

      {categories.map((cat) => {
        const list = grouped.get(cat.id) ?? [];
        return (
          <section key={cat.id} className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">{cat.name}</h2>
              <Btn size="sm" onClick={() => setCreatingInCategory(cat.id)}>
                + New question
              </Btn>
            </div>
            {list.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-500">
                No questions in this category yet.
              </div>
            ) : (
              <div className="space-y-2">
                {list.map((q, i) => (
                  <QuestionRow
                    key={q.id}
                    question={q}
                    index={i}
                    total={list.length}
                    onUp={() => moveWithinCategory(cat.id, q.id, -1)}
                    onDown={() => moveWithinCategory(cat.id, q.id, 1)}
                    onEdit={() => setEditing(q)}
                    onDelete={() => {
                      const children = questions.filter((c) => c.parent_id === q.id);
                      const message =
                        children.length > 0
                          ? `Delete "${q.label}"?\n\n${children.length} dependent question${children.length === 1 ? '' : 's'} reference${children.length === 1 ? 's' : ''} this as a parent and will become orphaned (never visible):\n${children.map((c) => `  • ${c.label}`).join('\n')}\n\nProceed?`
                          : `Delete "${q.label}"?`;
                      if (confirm(message)) deleteMutation.mutate(q.id);
                    }}
                    parents={questions}
                    documents={documents}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}

      {creatingInCategory && (
        <QuestionEditor
          mode="create"
          defaultCategoryId={creatingInCategory}
          questions={questions}
          categories={categories}
          documents={documents}
          onClose={() => setCreatingInCategory(null)}
        />
      )}
      {editing && (
        <QuestionEditor
          mode="edit"
          existing={editing}
          defaultCategoryId={editing.category_id}
          questions={questions}
          categories={categories}
          documents={documents}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function QuestionRow({
  question,
  index,
  total,
  onUp,
  onDown,
  onEdit,
  onDelete,
  parents,
  documents,
}: {
  question: Question;
  index: number;
  total: number;
  onUp: () => void;
  onDown: () => void;
  onEdit: () => void;
  onDelete: () => void;
  parents: Question[];
  documents: CompanyDocument[];
}) {
  const parent = parents.find((p) => p.id === question.parent_id);
  const adoptable = documents.find((d) => d.id === question.adoptable_doc_id);
  const noFollowups =
    question.type === 'yesno'
      ? parents.filter((c) => c.parent_id === question.id && c.condition?.value === false)
      : [];
  const optionFollowups =
    question.type === 'multiselect' || question.type === 'multiselect_uploads'
      ? parents.filter(
          (c) =>
            c.parent_id === question.id &&
            typeof c.condition?.value === 'string' &&
            (question.options ?? []).includes(c.condition.value),
        )
      : [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="flex flex-col">
          <button
            onClick={onUp}
            disabled={index === 0}
            className="px-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
            aria-label="Move up"
          >
            ▲
          </button>
          <button
            onClick={onDown}
            disabled={index === total - 1}
            className="px-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
            aria-label="Move down"
          >
            ▼
          </button>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-slate-500">{question.type}</span>
            {question.required && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">required</span>
            )}
            {question.requires_upload && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">upload</span>
            )}
            {parent && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                if “{parent.label}” = {String(question.condition?.value ?? '')}
              </span>
            )}
            {noFollowups.length > 0 && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                {noFollowups.length} follow-up{noFollowups.length === 1 ? '' : 's'} on “No”
              </span>
            )}
            {optionFollowups.length > 0 && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                {optionFollowups.length} option follow-up
                {optionFollowups.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className="mt-1 text-sm font-medium text-slate-900">{question.label}</div>
          {question.help_text && (
            <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>
          )}
          {question.options && question.options.length > 0 && (
            <div className="mt-1 text-xs text-slate-500">Options: {question.options.join(', ')}</div>
          )}
          {adoptable && (
            <div className="mt-1 text-xs text-slate-500">
              {question.type === 'document_upload' ? 'Adoptable alternative: ' : 'Adopt: '}
              {adoptable.name}
            </div>
          )}
          {question.adoption_triggers.length > 0 && (
            <div className="mt-1 text-xs text-slate-500">
              Triggers:{' '}
              {question.adoption_triggers
                .map((t) => `${t.trigger_value} → ${documents.find((d) => d.id === t.document_id)?.name ?? '?'}${t.adopt_only ? ' (adopt only)' : ''}`)
                .join(' · ')}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Btn size="sm" variant="secondary" onClick={onEdit}>Edit</Btn>
          <Btn size="sm" variant="ghost" onClick={onDelete}>Delete</Btn>
        </div>
      </div>
    </div>
  );
}

function QuestionEditor({
  mode,
  existing,
  defaultCategoryId,
  questions,
  categories,
  documents,
  onClose,
}: {
  mode: 'create' | 'edit';
  existing?: Question;
  defaultCategoryId: string;
  questions: Question[];
  categories: QuestionCategory[];
  documents: CompanyDocument[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<QuestionDraft>(() =>
    fromQuestion(existing, defaultCategoryId, questions),
  );
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setDraft(fromQuestion(existing, defaultCategoryId, questions));
  }, [existing, defaultCategoryId, questions]);

  const adoptableDocs = documents.filter((d) => d.is_adoptable && d.current_version);
  const possibleParents = questions.filter((q) => q.id !== existing?.id);
  const possibleFollowups = questions.filter((q) => q.id !== existing?.id);
  const followupsByCategory = useMemo(() => {
    const groups = new Map<string, Question[]>();
    for (const q of possibleFollowups) {
      const list = groups.get(q.category_id) ?? [];
      list.push(q);
      groups.set(q.category_id, list);
    }
    return groups;
  }, [possibleFollowups]);
  const previousFollowupIds = useMemo(
    () =>
      existing
        ? questions
            .filter((c) => c.parent_id === existing.id && c.condition?.value === false)
            .map((c) => c.id)
        : [],
    [questions, existing],
  );
  // childId → previously linked option string, for multiselect parents.
  const previousOptionFollowups = useMemo(() => {
    const map = new Map<string, string>();
    if (!existing) return map;
    if (existing.type !== 'multiselect' && existing.type !== 'multiselect_uploads') {
      return map;
    }
    for (const c of questions) {
      if (c.parent_id !== existing.id) continue;
      if (typeof c.condition?.value !== 'string') continue;
      map.set(c.id, c.condition.value);
    }
    return map;
  }, [questions, existing]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toApiPayload(draft);
      const saved =
        mode === 'create'
          ? await adminApi.post<{ question: Question }>('/api/questions', payload)
          : await adminApi.patch<{ question: Question }>(
              `/api/questions/${existing!.id}`,
              payload,
            );

      if (draft.type === 'yesno') {
        const parentId = saved.question.id;
        const desired = new Set(draft.no_followup_ids);
        const previous = new Set(previousFollowupIds);

        const toLink = draft.no_followup_ids.filter((id) => !previous.has(id));
        const toUnlink = previousFollowupIds.filter((id) => !desired.has(id));

        await Promise.all([
          ...toLink.map((id) =>
            adminApi.patch(`/api/questions/${id}`, {
              parent_id: parentId,
              condition: { value: false },
              required: true,
            }),
          ),
          ...toUnlink.map((id) =>
            adminApi.patch(`/api/questions/${id}`, {
              parent_id: null,
              condition: null,
            }),
          ),
        ]);
      }

      if (draft.type === 'multiselect' || draft.type === 'multiselect_uploads') {
        const parentId = saved.question.id;
        const savedOptions = new Set(saved.question.options ?? []);
        const desired = new Map<string, string>();
        for (const [opt, ids] of Object.entries(draft.option_followup_ids)) {
          if (!savedOptions.has(opt)) continue;
          for (const id of ids) desired.set(id, opt);
        }

        const childIds = new Set<string>([
          ...previousOptionFollowups.keys(),
          ...desired.keys(),
        ]);
        const ops: Array<Promise<unknown>> = [];
        for (const childId of childIds) {
          const prev = previousOptionFollowups.get(childId);
          const next = desired.get(childId);
          if (prev === next) continue;
          if (next !== undefined) {
            ops.push(
              adminApi.patch(`/api/questions/${childId}`, {
                parent_id: parentId,
                condition: { value: next },
                required: true,
              }),
            );
          } else {
            ops.push(
              adminApi.patch(`/api/questions/${childId}`, {
                parent_id: null,
                condition: null,
              }),
            );
          }
        }
        await Promise.all(ops);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questions'] });
      onClose();
    },
    onError: (err) => setError(err),
  });

  const requiresOptions =
    draft.type === 'select' ||
    draft.type === 'multiselect' ||
    draft.type === 'multiselect_uploads';
  const isLegacyAdoptType = draft.type === 'document_adopt';
  const isDocumentUpload = draft.type === 'document_upload';
  const isMultiselectUploads = draft.type === 'multiselect_uploads';
  const isSignature = draft.type === 'signature';
  const isAnyDocType = isLegacyAdoptType || isDocumentUpload;

  function setField<K extends keyof QuestionDraft>(key: K, value: QuestionDraft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'create' ? 'New question' : 'Edit question'}
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Category" required>
            <Select
              value={draft.category_id}
              onChange={(e) => setField('category_id', e.target.value)}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Type" required>
            <Select value={draft.type} onChange={(e) => setField('type', e.target.value as QuestionType)}>
              <option value="yesno">Yes / No</option>
              <option value="text">Text</option>
              <option value="select">Single select</option>
              <option value="multiselect">Multi select</option>
              <option value="multiselect_uploads">Multi select with uploads</option>
              <option value="document_upload">Document upload</option>
              <option value="signature">Signature</option>
              {isLegacyAdoptType && (
                <option value="document_adopt">Document adoption (legacy)</option>
              )}
            </Select>
          </Field>
        </div>

        <Field label="Required">
          <label className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={draft.required}
              onChange={(e) => setField('required', e.target.checked)}
            />
            Must be answered
          </label>
        </Field>

        <Field label="Label" required>
          <TextInput value={draft.label} onChange={(e) => setField('label', e.target.value)} />
        </Field>

        <Field label="Help text">
          <TextArea value={draft.help_text} onChange={(e) => setField('help_text', e.target.value)} rows={2} />
        </Field>

        {requiresOptions && !isMultiselectUploads && (
          <Field label="Options" required hint="One per line">
            <TextArea
              value={draft.options.join('\n')}
              onChange={(e) =>
                setField('options', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))
              }
            />
          </Field>
        )}

        {isMultiselectUploads && (
          <MultiselectUploadsOptionsEditor
            initialOptions={draft.options}
            initialTriggers={draft.adoption_triggers}
            adoptableDocs={adoptableDocs}
            onChange={({ options, triggers }) => {
              setDraft((d) => ({ ...d, options, adoption_triggers: triggers }));
            }}
          />
        )}

        {isDocumentUpload && (
          <Field
            label="Adoptable document (optional)"
            hint="If set, the company may adopt this standard policy instead of uploading their own."
          >
            <Select
              value={draft.adoptable_doc_id ?? ''}
              onChange={(e) => setField('adoptable_doc_id', e.target.value || null)}
            >
              <option value="">— None —</option>
              {adoptableDocs.map((d) => (
                <option key={d.id} value={d.id}>{d.name} ({d.version})</option>
              ))}
            </Select>
          </Field>
        )}

        {isLegacyAdoptType && (
          <Field label="Adoptable document" required hint="Legacy document adoption type">
            <Select
              value={draft.adoptable_doc_id ?? ''}
              onChange={(e) => setField('adoptable_doc_id', e.target.value || null)}
            >
              <option value="">— Select document —</option>
              {adoptableDocs.map((d) => (
                <option key={d.id} value={d.id}>{d.name} ({d.version})</option>
              ))}
            </Select>
          </Field>
        )}

        {!isAnyDocType && !isSignature && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={draft.requires_upload}
                onChange={(e) => setField('requires_upload', e.target.checked)}
              />
              Require file upload
            </div>
            {draft.requires_upload && (
              <Field label="Upload label" hint="Shown to the company above the file drop">
                <TextInput
                  value={draft.upload_label}
                  onChange={(e) => setField('upload_label', e.target.value)}
                />
              </Field>
            )}
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-2 text-sm font-semibold text-slate-700">Show only when…</div>
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Parent question"
              hint="Pick any question from any category."
            >
              <Select
                value={draft.parent_id ?? ''}
                onChange={(e) => setField('parent_id', e.target.value || null)}
              >
                <option value="">— None —</option>
                {categories.map((cat) => {
                  const inCat = possibleParents.filter((p) => p.category_id === cat.id);
                  if (inCat.length === 0) return null;
                  return (
                    <optgroup key={cat.id} label={cat.name}>
                      {inCat.map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </optgroup>
                  );
                })}
              </Select>
            </Field>
            <Field label="Parent answer equals" hint='Use "true"/"false" for yes/no parents'>
              <TextInput
                value={draft.condition_value}
                disabled={!draft.parent_id}
                onChange={(e) => setField('condition_value', e.target.value)}
              />
            </Field>
          </div>
        </div>

        {draft.type === 'yesno' && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-700">
              Follow-up questions when answered “No”
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Selected questions will only appear when this question is answered No, and
              will be marked as required. Questions from any category can be selected.
            </p>
            {possibleFollowups.length === 0 ? (
              <div className="mt-3 text-xs italic text-slate-500">
                No other questions exist yet. Create them first, then edit this question
                to link them.
              </div>
            ) : (
              <div className="mt-3 max-h-72 space-y-3 overflow-y-auto pr-1">
                {categories.map((cat) => {
                  const list = followupsByCategory.get(cat.id) ?? [];
                  if (list.length === 0) return null;
                  return (
                    <div key={cat.id}>
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        {cat.name}
                      </div>
                      {list.map((q) => {
                        const checked = draft.no_followup_ids.includes(q.id);
                        const conflict =
                          !checked &&
                          q.parent_id !== null &&
                          q.parent_id !== existing?.id;
                        const crossCategory = q.category_id !== draft.category_id;
                        return (
                          <label
                            key={q.id}
                            className="flex items-start gap-2 rounded px-2 py-1 text-sm text-slate-700 hover:bg-white"
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...draft.no_followup_ids, q.id]
                                  : draft.no_followup_ids.filter((id) => id !== q.id);
                                setField('no_followup_ids', next);
                              }}
                            />
                            <span className="flex-1">
                              <span className="font-medium">{q.label}</span>
                              <span className="ml-2 text-xs uppercase tracking-wide text-slate-500">
                                {q.type}
                              </span>
                              {crossCategory && (
                                <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">
                                  cross-category
                                </span>
                              )}
                              {conflict && (
                                <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                                  already linked to another parent — selecting will
                                  override
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {(draft.type === 'multiselect' || draft.type === 'multiselect_uploads') && (
          <MultiselectOptionFollowups
            options={draft.options}
            optionFollowupIds={draft.option_followup_ids}
            possibleFollowups={possibleFollowups}
            followupsByCategory={followupsByCategory}
            categories={categories}
            currentCategoryId={draft.category_id}
            existingId={existing?.id ?? null}
            onChange={(next) => setField('option_followup_ids', next)}
          />
        )}

        {!isAnyDocType && !isMultiselectUploads && (
          <AdoptionTriggersEditor
            triggers={draft.adoption_triggers}
            adoptableDocs={adoptableDocs}
            onChange={(triggers) => setField('adoption_triggers', triggers)}
            optionsList={null}
            heading="Adoption triggers"
            help="When the answer matches a trigger value, the company is shown the linked policy to adopt."
          />
        )}

        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}

function MultiselectOptionFollowups({
  options,
  optionFollowupIds,
  possibleFollowups,
  followupsByCategory,
  categories,
  currentCategoryId,
  existingId,
  onChange,
}: {
  options: string[];
  optionFollowupIds: Record<string, string[]>;
  possibleFollowups: Question[];
  followupsByCategory: Map<string, Question[]>;
  categories: QuestionCategory[];
  currentCategoryId: string;
  existingId: string | null;
  onChange: (next: Record<string, string[]>) => void;
}) {
  const cleanOptions = options.map((o) => o.trim()).filter(Boolean);

  // For each child, find which option (if any) it's currently linked to
  // within this draft, so we can show conflict hints when a user tries to
  // assign it to a second option.
  const childToOption = new Map<string, string>();
  for (const [opt, ids] of Object.entries(optionFollowupIds)) {
    for (const id of ids) childToOption.set(id, opt);
  }

  function toggle(option: string, childId: string, checked: boolean) {
    const next: Record<string, string[]> = {};
    for (const [opt, ids] of Object.entries(optionFollowupIds)) {
      next[opt] = ids.filter((id) => id !== childId);
    }
    if (!next[option]) next[option] = [];
    if (checked) next[option] = [...next[option], childId];
    onChange(next);
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="text-sm font-semibold text-slate-700">
        Follow-up questions per option
      </div>
      <p className="mt-1 text-xs text-slate-500">
        For each option, tick the questions that should appear (and be required)
        when that option is selected. A question can only be a follow-up for
        one option of this multi-select at a time.
      </p>
      {cleanOptions.length === 0 ? (
        <div className="mt-3 text-xs italic text-slate-500">
          Add at least one option above to configure per-option follow-ups.
        </div>
      ) : possibleFollowups.length === 0 ? (
        <div className="mt-3 text-xs italic text-slate-500">
          No other questions exist yet. Create them first, then edit this
          question to link them.
        </div>
      ) : (
        <div className="mt-3 space-y-4">
          {cleanOptions.map((option) => {
            const checkedIds = new Set(optionFollowupIds[option] ?? []);
            return (
              <div key={option} className="rounded border border-slate-200 bg-white p-3">
                <div className="mb-2 text-sm font-medium text-slate-800">
                  If “{option}” is ticked, show…
                </div>
                <div className="max-h-60 space-y-3 overflow-y-auto pr-1">
                  {categories.map((cat) => {
                    const list = followupsByCategory.get(cat.id) ?? [];
                    if (list.length === 0) return null;
                    return (
                      <div key={cat.id}>
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          {cat.name}
                        </div>
                        {list.map((q) => {
                          const checked = checkedIds.has(q.id);
                          const linkedElsewhereInDraft =
                            !checked &&
                            childToOption.has(q.id) &&
                            childToOption.get(q.id) !== option;
                          const linkedToOtherParent =
                            !checked &&
                            !childToOption.has(q.id) &&
                            q.parent_id !== null &&
                            q.parent_id !== existingId;
                          const crossCategory =
                            q.category_id !== currentCategoryId;
                          return (
                            <label
                              key={q.id}
                              className="flex items-start gap-2 rounded px-2 py-1 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={checked}
                                onChange={(e) =>
                                  toggle(option, q.id, e.target.checked)
                                }
                              />
                              <span className="flex-1">
                                <span className="font-medium">{q.label}</span>
                                <span className="ml-2 text-xs uppercase tracking-wide text-slate-500">
                                  {q.type}
                                </span>
                                {crossCategory && (
                                  <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">
                                    cross-category
                                  </span>
                                )}
                                {linkedElsewhereInDraft && (
                                  <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                                    already used by “{childToOption.get(q.id)}” —
                                    selecting will move it
                                  </span>
                                )}
                                {linkedToOtherParent && (
                                  <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                                    already linked to another parent —
                                    selecting will override
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type OptionRow = { option: string; document_id: string; adopt_only: boolean };

function MultiselectUploadsOptionsEditor({
  initialOptions,
  initialTriggers,
  adoptableDocs,
  onChange,
}: {
  initialOptions: string[];
  initialTriggers: DraftTrigger[];
  adoptableDocs: CompanyDocument[];
  onChange: (next: { options: string[]; triggers: DraftTrigger[] }) => void;
}) {
  const [rows, setRows] = useState<OptionRow[]>(() => {
    const tByOpt = new Map(initialTriggers.map((t) => [t.trigger_value, t]));
    const base = initialOptions.map((opt) => {
      const t = tByOpt.get(opt);
      return {
        option: opt,
        document_id: t?.document_id ?? '',
        adopt_only: t?.adopt_only ?? false,
      };
    });
    return base.length > 0 ? base : [{ option: '', document_id: '', adopt_only: false }];
  });

  function emit(next: OptionRow[]) {
    setRows(next);
    onChange({
      options: next.map((r) => r.option),
      triggers: next
        .filter((r) => r.option.trim() && r.document_id)
        .map((r) => ({
          trigger_value: r.option,
          document_id: r.document_id,
          adopt_only: r.adopt_only,
        })),
    });
  }

  function update(i: number, patch: Partial<OptionRow>) {
    emit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function remove(i: number) {
    emit(rows.filter((_, idx) => idx !== i));
  }
  function add() {
    emit([...rows, { option: '', document_id: '', adopt_only: false }]);
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700">Answer options</div>
        <Btn size="sm" variant="secondary" onClick={add}>+ Add option</Btn>
      </div>
      <p className="mb-3 text-xs text-slate-500">
        Each option is an answer the company can select. For every selected answer the company
        gets its own document upload zone. Optionally link a standard adoptable document so the
        company can adopt it instead of uploading their own.
      </p>
      {rows.length === 0 && (
        <div className="text-xs italic text-slate-500">No options yet.</div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="mb-2 grid grid-cols-12 items-center gap-2">
          <div className="col-span-4">
            <TextInput
              placeholder="Option label"
              value={r.option}
              onChange={(e) => update(i, { option: e.target.value })}
            />
          </div>
          <div className="col-span-5">
            <Select
              value={r.document_id}
              onChange={(e) =>
                update(i, {
                  document_id: e.target.value,
                  adopt_only: e.target.value ? r.adopt_only : false,
                })
              }
            >
              <option value="">— No adoptable doc —</option>
              {adoptableDocs.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </Select>
          </div>
          <label
            className="col-span-2 inline-flex items-center gap-1 text-xs text-slate-700"
            title={r.document_id ? '' : 'Link an adoptable document to enable'}
          >
            <input
              type="checkbox"
              disabled={!r.document_id}
              checked={r.adopt_only}
              onChange={(e) => update(i, { adopt_only: e.target.checked })}
            />
            Adopt only
          </label>
          <div className="col-span-1">
            <Btn size="sm" variant="ghost" onClick={() => remove(i)}>×</Btn>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdoptionTriggersEditor({
  triggers,
  adoptableDocs,
  onChange,
  optionsList,
  heading,
  help,
}: {
  triggers: DraftTrigger[];
  adoptableDocs: CompanyDocument[];
  onChange: (next: DraftTrigger[]) => void;
  optionsList: string[] | null;
  heading: string;
  help: string;
}) {
  function add() {
    onChange([...triggers, { trigger_value: '', document_id: '', adopt_only: false }]);
  }
  function update(i: number, patch: Partial<DraftTrigger>) {
    onChange(triggers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }
  function remove(i: number) {
    onChange(triggers.filter((_, idx) => idx !== i));
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700">{heading}</div>
        <Btn size="sm" variant="secondary" onClick={add}>+ Add</Btn>
      </div>
      <p className="mb-3 text-xs text-slate-500">{help}</p>
      {triggers.length === 0 && (
        <div className="text-xs italic text-slate-500">None configured.</div>
      )}
      {triggers.map((t, i) => (
        <div key={i} className="mb-2 grid grid-cols-12 gap-2">
          <div className="col-span-3">
            {optionsList ? (
              <Select
                value={t.trigger_value}
                onChange={(e) => update(i, { trigger_value: e.target.value })}
              >
                <option value="">— Option —</option>
                {optionsList.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </Select>
            ) : (
              <TextInput
                placeholder="Answer value"
                value={t.trigger_value}
                onChange={(e) => update(i, { trigger_value: e.target.value })}
              />
            )}
          </div>
          <div className="col-span-6">
            <Select
              value={t.document_id}
              onChange={(e) => update(i, { document_id: e.target.value })}
            >
              <option value="">— Select document —</option>
              {adoptableDocs.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </Select>
          </div>
          <label className="col-span-2 inline-flex items-center gap-1 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={t.adopt_only}
              onChange={(e) => update(i, { adopt_only: e.target.checked })}
            />
            Adopt only
          </label>
          <div className="col-span-1">
            <Btn size="sm" variant="ghost" onClick={() => remove(i)}>×</Btn>
          </div>
        </div>
      ))}
    </div>
  );
}

function fromQuestion(
  q: Question | undefined,
  defaultCategoryId: string,
  allQuestions: Question[] = [],
): QuestionDraft {
  if (!q) return emptyDraft(defaultCategoryId);
  return {
    type: q.type,
    category_id: q.category_id,
    label: q.label,
    help_text: q.help_text ?? '',
    required: q.required,
    requires_upload: q.requires_upload,
    upload_label: q.upload_label ?? '',
    options: q.options ?? [],
    parent_id: q.parent_id,
    condition_value: q.condition?.value !== undefined ? String(q.condition.value) : '',
    adoptable_doc_id: q.adoptable_doc_id,
    adoption_triggers: q.adoption_triggers.map((t) => ({
      trigger_value: t.trigger_value,
      document_id: t.document_id,
      adopt_only: t.adopt_only,
    })),
    no_followup_ids: allQuestions
      .filter((c) => c.parent_id === q.id && c.condition?.value === false)
      .map((c) => c.id),
    option_followup_ids:
      q.type === 'multiselect' || q.type === 'multiselect_uploads'
        ? Object.fromEntries(
            (q.options ?? []).map((opt) => [
              opt,
              allQuestions
                .filter(
                  (c) => c.parent_id === q.id && c.condition?.value === opt,
                )
                .map((c) => c.id),
            ]),
          )
        : {},
  };
}

function toApiPayload(d: QuestionDraft): Record<string, unknown> {
  const isDocType = d.type === 'document_adopt' || d.type === 'document_upload';
  const isSignature = d.type === 'signature';
  const suppressUpload = isDocType || isSignature;
  const payload: Record<string, unknown> = {
    type: d.type,
    category_id: d.category_id,
    label: d.label.trim(),
    help_text: d.help_text.trim() || null,
    required: d.required,
    requires_upload: suppressUpload ? false : d.requires_upload,
    upload_label:
      !suppressUpload && d.requires_upload ? d.upload_label.trim() || null : null,
    options:
      d.type === 'select' || d.type === 'multiselect' || d.type === 'multiselect_uploads'
        ? d.options.map((s) => s.trim()).filter(Boolean)
        : null,
    parent_id: d.parent_id || null,
    condition: d.parent_id
      ? { value: parseConditionValue(d.condition_value) }
      : null,
    adoptable_doc_id: isDocType ? d.adoptable_doc_id : null,
    adoption_triggers:
      isDocType || isSignature
        ? []
        : d.adoption_triggers.filter((t) => t.trigger_value && t.document_id),
  };
  return payload;
}

function parseConditionValue(raw: string): string | boolean | string[] {
  const trimmed = raw.trim();
  if (trimmed.toLowerCase() === 'true') return true;
  if (trimmed.toLowerCase() === 'false') return false;
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      // fall through
    }
  }
  return trimmed;
}
